/**
 * Segnalazioni — form di descrizione del problema (spec §5).
 *
 * Testo libero con domande chiave suggerite (placeholder + hint). Nulla è
 * obbligatorio. Anteprima del contesto raccolto come chip.
 * Stile: inline styles con design tokens `--tl4k-*` (pattern admin-ui).
 */
import { useMemo, useState, type CSSProperties } from 'react';
import { Send } from 'lucide-react';
import type { ClientContext } from './types';

/** Domande chiave suggerite (nessuna obbligatoria). */
export const ISSUE_KEY_QUESTIONS = [
  'Cosa stavi facendo?',
  'Cosa è successo?',
  'Cosa ti aspettavi?',
  'Era già successo?',
] as const;

export interface IssueReportFormProps {
  /** Contesto raccolto (anteprima chip). */
  context: ClientContext | null;
  /** Invio: il caller decide createStudentIssue/createTeacherIssue. */
  onSubmit: (description: string) => Promise<void> | void;
  /** Invio in corso (disabilita submit). */
  submitting?: boolean;
  /** Errore di invio da mostrare. */
  error?: string | null;
  /** Titolo del form. Default: "Descrivi il problema". */
  title?: string;
}

const chipStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--tl4k-spacing-xs)',
  padding: '2px var(--tl4k-spacing-sm)',
  borderRadius: 'var(--tl4k-border-radius-pill)',
  background: 'var(--tl4k-neutral-100)',
  border: '1px solid var(--tl4k-neutral-300)',
  color: 'var(--tl4k-neutral-700)',
  fontSize: 12,
  fontFamily: 'var(--tl4k-font-family-body)',
  maxWidth: 260,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/** Estrae le chip di anteprima dal contesto raccolto. */
export function buildContextChips(context: ClientContext): Array<{ label: string; value: string }> {
  const chips: Array<{ label: string; value: string }> = [];
  if (context.app) chips.push({ label: 'App', value: context.app });
  if (context.mode) chips.push({ label: 'Modalità', value: context.mode });
  if (context.os) chips.push({ label: 'OS', value: context.os });
  if (context.viewport) chips.push({ label: 'Viewport', value: context.viewport });
  if (context.screen) chips.push({ label: 'Schermo', value: context.screen });
  if (context.language) chips.push({ label: 'Lingua', value: context.language });
  if (context.session_pin) chips.push({ label: 'PIN sessione', value: context.session_pin });
  if (context.pseudonym?.formatted) chips.push({ label: 'Pseudonimo', value: context.pseudonym.formatted });
  if (context.user?.alias) chips.push({ label: 'Utente', value: context.user.alias });
  else if (context.user?.email) chips.push({ label: 'Utente', value: context.user.email });
  if (context.techy_client?.daemon_version) {
    chips.push({
      label: 'techy-client',
      value: `daemon ${context.techy_client.daemon_version}${context.techy_client.hostname ? ` · ${context.techy_client.hostname}` : ''}`,
    });
  }
  return chips;
}

/**
 * Form segnalazione: testo libero (domande chiave come suggerimento) +
 * anteprima contesto + submit. Nessun campo obbligatorio.
 */
export function IssueReportForm({
  context,
  onSubmit,
  submitting = false,
  error,
  title = 'Descrivi il problema',
}: IssueReportFormProps) {
  const [description, setDescription] = useState('');
  const chips = useMemo(() => (context ? buildContextChips(context) : []), [context]);

  const placeholder = useMemo(() => ISSUE_KEY_QUESTIONS.join('\n'), []);

  const handleSubmit = () => {
    if (submitting) return;
    void onSubmit(description.trim());
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--tl4k-spacing-md)',
        fontFamily: 'var(--tl4k-font-family-body)',
      }}
    >
      <h3
        style={{
          margin: 0,
          fontSize: 16,
          fontWeight: 700,
          color: 'var(--tl4k-neutral-800)',
        }}
      >
        {title}
      </h3>

      {/* Suggerimenti: domande chiave (cliccabili → aggiungono al testo) */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--tl4k-spacing-xs)' }}>
        {ISSUE_KEY_QUESTIONS.map((question) => (
          <button
            key={question}
            type="button"
            onClick={() =>
              setDescription((prev) => (prev ? `${prev.trimEnd()}\n${question}\n` : `${question}\n`))
            }
            title={`Aggiungi la domanda "${question}" come traccia`}
            style={{
              padding: '2px var(--tl4k-spacing-sm)',
              borderRadius: 'var(--tl4k-border-radius-pill)',
              border: '1px dashed var(--tl4k-primary-300)',
              background: 'var(--tl4k-primary-50)',
              color: 'var(--tl4k-primary-700)',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            {question}
          </button>
        ))}
      </div>

      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder={placeholder}
        rows={6}
        maxLength={8000}
        aria-label="Descrizione del problema (facoltativa)"
        style={{
          width: '100%',
          boxSizing: 'border-box',
          resize: 'vertical',
          padding: 'var(--tl4k-spacing-sm)',
          borderRadius: 'var(--tl4k-border-radius-md)',
          border: '1px solid var(--tl4k-neutral-300)',
          fontFamily: 'var(--tl4k-font-family-body)',
          fontSize: 14,
          color: 'var(--tl4k-neutral-800)',
          background: 'var(--tl4k-surface-default)',
        }}
      />

      {/* Anteprima contesto raccolto */}
      {chips.length > 0 && (
        <div>
          <p
            style={{
              margin: '0 0 var(--tl4k-spacing-xs)',
              fontSize: 12,
              color: 'var(--tl4k-neutral-600)',
            }}
          >
            Dati raccolti automaticamente (inviati insieme alla segnalazione):
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--tl4k-spacing-xs)' }}>
            {chips.map((chip) => (
              <span key={`${chip.label}-${chip.value}`} style={chipStyle} title={`${chip.label}: ${chip.value}`}>
                <strong>{chip.label}:</strong> {chip.value}
              </span>
            ))}
          </div>
        </div>
      )}

      {error && (
        <p
          role="alert"
          style={{
            margin: 0,
            padding: 'var(--tl4k-spacing-sm)',
            borderRadius: 'var(--tl4k-border-radius-md)',
            background: 'var(--tl4k-error-surface)',
            color: 'var(--tl4k-error)',
            fontSize: 13,
          }}
        >
          {error}
        </p>
      )}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={submitting}
        style={{
          alignSelf: 'flex-end',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--tl4k-spacing-xs)',
          padding: 'var(--tl4k-spacing-sm) var(--tl4k-spacing-lg)',
          borderRadius: 'var(--tl4k-border-radius-md)',
          border: 'none',
          background: submitting ? 'var(--tl4k-neutral-400)' : 'var(--tl4k-primary-500)',
          color: 'var(--tl4k-surface-default)',
          fontWeight: 600,
          fontSize: 14,
          cursor: submitting ? 'not-allowed' : 'pointer',
        }}
      >
        <Send size={14} aria-hidden />
        {submitting ? 'Invio in corso…' : 'Invia segnalazione'}
      </button>
    </div>
  );
}

export default IssueReportForm;
